import {
  useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';
import useGetMetric from 'hooks/useGetMetric';
import Button from 'components/Button';
import MaskInput from 'components/MaskedInput';
import Select from 'components/Select';
import { IData } from 'features/ProductConverter';
import { numberFormatter, unMaskInput } from 'utils/helpers';
import { AlkaliProps, SelectProps } from 'utils/types';

import resetIcon from '/resetIcon.svg';

interface Props {
  data: AlkaliProps[];
  omyaData: AlkaliProps[];
  setComparisonData: (data: IData | null) => void;
  back: () => void;
  product?: string;
  setGeneretedBrief: (value: boolean) => void;
}

const ApplicationIndustryProduct = ({
  data,
  omyaData,
  setComparisonData,
  back,
  product,
  setGeneretedBrief,
}: Props) => {
  const { t } = useTranslation();
  const [consumption, setConsumption] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [costDelivered, setCostDelivered] = useState<string>('');
  const [alkali, setAlkali] = useState<SelectProps | null>(null);
  const [concentration, setConcentration] = useState<SelectProps | null>(null);
  const [omya, setOmya] = useState<SelectProps | null>(null);
  const [concentrationOmya, setConcentrationOmya] =
    useState<SelectProps | null>(null);
  const { ton } = useGetMetric();
  const { currencyBlue } = useCurrency();

  const annualExpenditureValue = useMemo(() => {
    const watchPrice1 = unMaskInput(price);
    const watchConsumption1 = unMaskInput(consumption);

    if (price?.length > 0 && consumption?.length) {
      return numberFormatter(Number(watchPrice1) * Number(watchConsumption1));
    } else {
      return 0;
    }
  }, [price, consumption]);

  useEffect(() => {
    if (product) {
      const productOmya = { value: product,
        label: product };
      setOmya(productOmya);
    }
  }, [product]);

  const onSubmit = () => {
    const result = {
      consumption,
      price,
      costDelivered,
      alkali: alkali?.value ? alkali?.value : '',
      concentration: concentration?.value ? concentration?.value : '',
      omya: omya?.value ? omya?.value : '',
      concentrationOmya: concentrationOmya?.value
        ? concentrationOmya?.value
        : '',
      annualExpenditure: annualExpenditureValue,
    };
    setComparisonData(result);
    setGeneretedBrief(true);
  };

  const alkaliSelectOptions = useMemo(() => data?.map((alkali) => ({
    value: alkali?.['Material A'],
    label: alkali?.['Material A'],
  })), [data]);

  function getUniqueListBy(arr: SelectProps[], key: string) {
    return [...new Map(arr?.map((item: any) => [item[key], item])).values()];
  }

  const arr1 = getUniqueListBy(alkaliSelectOptions, 'value');

  const getConcentrationList = useMemo(() => {
    const result: AlkaliProps[] = [];

    if (alkali?.value) {
      data?.forEach((alkaliItem: AlkaliProps) => {
        if (alkaliItem?.['Material A'] === alkali?.value) {
          result.push(alkaliItem);
        }
      });
    }

    return result.map((concentration: AlkaliProps) => ({
      value: String(concentration.Concentration),
      label:
          String(concentration.Concentration) === '100'
            ? String(concentration.Concentration) + ' = Powder'
            : String(concentration.Concentration) + ' %',
    }));
  }, [alkali]);

  const getConcentrationListOmya = useMemo(() => {
    let result: AlkaliProps[] = [];

    if (omya?.value) {
      result = omyaData.filter((omyaItem) => {
        if (omyaItem?.['Material A']) {
          const formatted = omyaItem['Material A']
            .replace(/ /g, '')
            .toLocaleLowerCase();

          return formatted.includes(
            omya?.value?.replace(/ /g, '').toLocaleLowerCase(),
          );
        }

        return false;
      });
    }

    return result.map((concentration: AlkaliProps) => ({
      value: String(concentration.Concentration),
      label: `${concentration.Concentration} ${
        concentration.Concentration === 100 ? ' = Powder' : ' %'
      }`,
    }));
  }, [omya]);

  useEffect(() => {
    if (getConcentrationList[0]) {
      setConcentration(getConcentrationList[0]);
    }
  }, [alkali]);

  useEffect(() => {
    if (getConcentrationListOmya[0]) {
      setConcentrationOmya(getConcentrationListOmya[0]);
    }
  }, [omya]);

  const resetData = () => {
    setConsumption('');
    setPrice('');
    setCostDelivered('');
    setAlkali(null);
    setConcentration(null);
    setComparisonData(null);
  };

  const disabledState = useMemo(() => {
    if (
      alkali?.value &&
      omya?.value &&
      concentration?.value &&
      concentrationOmya?.value &&
      price?.length > 0 &&
      costDelivered?.length > 0 &&
      consumption?.length
    ) {
      return false;
    }

    return true;
  }, [alkali, consumption, price, concentration, costDelivered]);

  const disabledStateSkip = useMemo(() => {
    if (
      alkali?.value &&
      omya?.value &&
      concentration?.value &&
      concentrationOmya?.value &&
      price?.length > 0 &&
      costDelivered?.length > 0 &&
      consumption?.length
    ) {
      return true;
    }

    return false;
  }, [alkali, consumption, price, concentration, costDelivered]);

  return (
    <form>
      <div className="border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl">
        <div className="flex justify-between md:flex-col">
          <div className="w-1/2 border-r border-primitiveGrey md:border-b md:border-r-0 pr-8 md:w-full md:pr-0">
            <div className="text-black font-bold text-xl md:text-lg">
              {t('currentProductTitle')}
            </div>
            <div className="my-6">
              <Select
                value={
                  alkali?.value
                    ? [
                      {
                        value: alkali.value,
                        label: alkali.value,
                      },
                    ]
                    : []
                }
                options={arr1}
                placeholder={t('selectValue')}
                title={t('currentAlkali')}
                onChange={(value) => setAlkali(value)}
              />
            </div>
            <div className="my-6">
              <Select
                value={
                  concentration?.value
                    ? [
                      {
                        value: concentration.value,
                        label:
                            concentration.value === '100'
                              ? concentration.value + ' = Powder'
                              : concentration.value + ' %',
                      },
                    ]
                    : []
                }
                options={getConcentrationList}
                placeholder={t('selectValue')}
                title={`${t('concentration')} (wt.%)`}
                onChange={(value) => setConcentration(value)}
              />
            </div>
            <div className="my-6 w-full">
              <MaskInput
                titleClassName="top-[-9px] left-[8px]"
                className="w-full"
                title={`${t('annualConsumption')} (${ton})`}
                placeholder={t('enterAnnualConsumption')}
                onChange={(val) => setConsumption(val)}
                value={consumption}
              />
            </div>
            <div className="my-6 w-full">
              <MaskInput
                titleClassName="top-[-9px] left-[8px]"
                className="w-full"
                title={`${t('price')} (${currencyBlue}/${ton})`}
                placeholder={t('enterPrice')}
                onChange={(val) => setPrice(val)}
                value={price}
              />
            </div>
            <div className="my-6 w-full relative">
              <div className="absolute top-[-10px] left-2 text-sm text-black">
                {t('currentExpenditure')}
              </div>
              <div className="bg-[#e2e2e2] p-3 rounded-xl text-secondaryText cursor-not-allowed">
                {annualExpenditureValue ? annualExpenditureValue : '0'}{' '}
                {currencyBlue}/{t('year')}
              </div>
            </div>
          </div>
          <div className="w-1/2 pl-8 md:w-full md:pl-0">
            <div className="text-black font-bold text-xl md:text-lg md:mt-6">
              {t('omyaOffered')}
            </div>
            <div className="my-6">
              <Select
                value={
                  omya?.value
                    ? [
                      {
                        value: omya.value,
                        label: omya.value,
                      },
                    ]
                    : []
                }
                options={[]}
                placeholder={t('selectValue')}
                title={t('product')}
                onChange={(value) => setOmya(value)}
              />
            </div>
            <div className="my-6">
              <Select
                value={
                  concentrationOmya?.value
                    ? [
                      {
                        value: concentrationOmya.value,
                        label:
                            concentrationOmya.value === '100'
                              ? concentrationOmya.value + ' = Powder'
                              : concentrationOmya.value + ' %',
                      },
                    ]
                    : []
                }
                options={getConcentrationListOmya}
                placeholder={t('selectValue')}
                title={`${t('concentration')} (wt.%)`}
                onChange={(value) => setConcentrationOmya(value)}
              />
            </div>
            <div className="my-6 w-full">
              <MaskInput
                titleClassName="top-[-9px] left-[8px]"
                className="w-full"
                title={`${t('costDelivered')} (${currencyBlue}/${ton})`}
                placeholder={t('enterAnnualConsumption')}
                onChange={(value) => setCostDelivered(value)}
                value={costDelivered}
              />
            </div>

            <div className="flex items-center justify-center my-8 my-6 font-medium text-primary md:mb-0">
              <div
                className="cursor-pointer flex items-center"
                onClick={resetData}
              >
                <span className="mr-3">
                  <img src={resetIcon} alt="" />
                </span>
                {t('resetData')}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-center md:mt-6">
          <div className="flex md:flex-col">
            <div className="flex">
              <Button
                className="w-[220px] mr-6 md:w-[159px] md:mr-2"
                btnType="secondary"
                onClick={() => back()}
              >
                {t('back')}
              </Button>
              <Button
                className={`w-[220px] md:w-[159px] ${
                  disabledStateSkip && 'border border-[#AFAFAF]'
                }`}
                btnType="secondary"
                onClick={() => setGeneretedBrief(true)}
                disabled={disabledStateSkip}
              >
                {t('skipToBrief')}
              </Button>
            </div>
            <div className="md:mt-3 md:flex md:justify-center">
              <Button
                className="w-[220px] ml-6 md:ml-0"
                disabled={disabledState}
                onClick={onSubmit}
              >
                {t('calculateIntoBrief')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ApplicationIndustryProduct;
